/**
 * The AI's running understanding of the call in progress.
 *
 * Fed by the transcript the HUD is already streaming: as `useCallTranscriptStream`
 * delivers segments, this forwards the ones the extractor has not seen and keeps
 * whatever comes back. No polling — the push response IS the current intake.
 *
 * Deliberately quiet about failure. This is prefill for a form the agent can
 * fill by hand, so a extractor that is down, a tenant with the feature off, or a
 * call nobody has spoken on yet all look the same: no fields, no error, no noise
 * on a screen someone is using to talk to a customer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  callIntakeApi,
  type CallIntake,
  type CallIntakeFields,
  type IntakeSegment,
} from '@/lib/calls/intakeApi';

/**
 * The shape this needs from a transcript segment, declared structurally rather
 * than imported. The live-transcript stream hook produces a richer object, and
 * anything with these three properties satisfies this — so the intake does not
 * take a build dependency on where the transcript happens to come from.
 */
export interface IntakeSourceSegment {
  seq: number;
  text: string;
  track?: string | null;
}

/**
 * How long to wait after the last new segment before pushing. Speech arrives in
 * bursts of short segments, and a push per segment would be a request per breath
 * — the extractor buffers anything below its own batch threshold anyway, so the
 * only thing per-segment pushing buys is HTTP traffic.
 */
const PUSH_DEBOUNCE_MS = 1_200;

/**
 * How long a silence has to last before we make the extractor read what it is
 * holding.
 *
 * The extractor waits for two segments before spending a pass, which is right
 * while somebody is mid-sentence and wrong the moment they stop. A customer who
 * gives their address and then waits for the agent leaves ONE segment in the
 * buffer, and it stayed there until they spoke again — so the address only
 * appeared after the next thing they said, and every value on the panel read
 * one utterance behind the call. A pause is the point at which a held segment
 * is worth a pass on its own.
 */
const SILENCE_FLUSH_MS = 3_500;

/** What the agent may type over. `jobTypeName` is derived from the id upstream. */
export type IntakeEdits = Partial<Omit<CallIntakeFields, 'jobTypeName'>>;

export interface UseCallIntakeResult {
  intake: CallIntake | null;
  /** True while a push is in flight — the panel shows a quiet "listening" hint. */
  busy: boolean;
  /** True while an agent's edit is being saved. */
  saving: boolean;
  /**
   * Record the agent's own answer for one or more fields. Resolves true when it
   * saved; false when it did not, so the row can put the value back rather than
   * showing an edit that only exists in this browser.
   */
  save: (edits: IntakeEdits) => Promise<boolean>;
}

export function useCallIntake(
  callControlId: string | null | undefined,
  segments: IntakeSourceSegment[],
  enabled = true,
): UseCallIntakeResult {
  const [intake, setIntake] = useState<CallIntake | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  // Bumped by every saved edit. A push that started BEFORE an edit landed
  // carries a view from before it, and applying that response would wipe the
  // agent's answer off the screen a second after they typed it — the server
  // keeps the edit either way, so the stale response is simply dropped.
  const saveSeqRef = useRef(0);

  // Highest seq the extractor has acknowledged. Kept in a ref so a push in
  // flight does not re-render, and reset with the call so one call's progress
  // can never suppress the next call's first push.
  const pushedThroughRef = useRef<number>(-1);
  /**
   * The high-water mark the last silence flush was sent for.
   *
   * Keeps a long silence to ONE "they have stopped talking" request instead of
   * one every time the timer is rearmed.
   */
  const flushedThroughRef = useRef<number>(-1);
  const inFlightRef = useRef(false);
  /**
   * Work that arrived while a push was in flight, and how urgently it wants
   * sending. `null` means nothing is queued.
   *
   * Not a queue of segments — the pump recomputes what is outstanding when it
   * runs — but the MODE has to survive, because the two are not
   * interchangeable. A silence flush landing behind an ordinary push used to be
   * remembered as merely "there is more", and the replay went out with the
   * original `final: false`: the extractor was never told the caller had
   * stopped talking, so the last thing they said sat in the buffer until they
   * said something else. That is the exact failure the flush exists to prevent,
   * so a queued flush outranks a queued push.
   */
  const queuedFlushRef = useRef<boolean | null>(null);

  /**
   * The call the hook is on RIGHT NOW, for responses that outlive their call.
   *
   * A push carries the id it was sent for; if the dock has moved to another
   * call by the time it comes back, everything it says is about a conversation
   * this panel is no longer showing. Applying it would put customer A's
   * extracted details under customer B's name — and worse, mark B's opening
   * segments as already delivered, so they would never be sent at all.
   *
   * `saveSeqRef` cannot catch this: it is reset to 0 on the swap, so a stale
   * response's `seqAtStart` of 0 matches and sails through.
   */
  const currentCallRef = useRef(callControlId);

  useEffect(() => {
    currentCallRef.current = callControlId;
    setIntake(null);
    // Including `busy`, because the guard below deliberately makes a stale
    // response skip its own cleanup — it must not clear an inFlight flag that
    // now belongs to the new call. Nothing else would ever turn this off, so
    // the new call inherited "Listening…" from the old one and kept it until it
    // pushed something of its own. On a caller who never speaks, forever.
    setBusy(false);
    pushedThroughRef.current = -1;
    flushedThroughRef.current = -1;
    inFlightRef.current = false;
    queuedFlushRef.current = null;
    saveSeqRef.current = 0;
  }, [callControlId]);

  // The transcript as it stands, read by the pump rather than closed over — the
  // pump has to see what has arrived SINCE it was scheduled, not what existed
  // when the effect ran.
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;

  /**
   * Send whatever the extractor has not read yet.
   *
   * One push at a time, because overlapping pushes race on the same row and the
   * later response can be the older view. A push that arrives while one is in
   * flight is QUEUED, never dropped: dropping it is what made the panel lag a
   * whole utterance behind — the batch was skipped, and nothing re-sent it until
   * the customer happened to say something else.
   *
   * `flush` asks the extractor to read a short backlog rather than hold it for
   * the next batch. It is the same switch the end-of-call pass uses; from here
   * it means "they have stopped talking", which is exactly when a single held
   * segment stops being worth waiting on.
   */
  const pump = useCallback(
    async (flush: boolean) => {
      if (!enabled || !callControlId) return;
      if (inFlightRef.current) {
        // A flush queued behind a push must still go out as a flush.
        queuedFlushRef.current = (queuedFlushRef.current ?? false) || flush;
        return;
      }

      const pending = segmentsRef.current.filter(
        (segment) => segment.seq > pushedThroughRef.current && segment.text.trim(),
      );
      if (pending.length === 0) {
        // A flush with nothing new is NOT a no-op, and treating it as one left
        // the whole feature half-working: the extractor holds a batch below its
        // own threshold, so the single segment carrying the address was already
        // delivered and still unread. `final: true` is the only thing that
        // makes it read what it is holding — the silence is exactly when there
        // is nothing new left to send, so bailing here meant the flush fired
        // precisely never in the case it exists for.
        //
        // Bounded to once per high-water mark: one request per silence, not one
        // per timer, and nothing at all on a call where no one has spoken yet.
        if (!flush) return;
        if (pushedThroughRef.current < 0) return;
        if (flushedThroughRef.current === pushedThroughRef.current) return;
      }

      inFlightRef.current = true;
      setBusy(true);

      const batch: IntakeSegment[] = pending.map((segment) => ({
        seq: segment.seq,
        text: segment.text,
        track: segment.track,
      }));
      const highest = batch.length ? batch[batch.length - 1].seq : pushedThroughRef.current;
      const seqAtStart = saveSeqRef.current;

      try {
        const next = await callIntakeApi.push(callControlId, { segments: batch, final: flush });
        // Nothing from a call the panel has moved on from — not the intake, and
        // not the delivery mark either. Marking A's high water line as reached
        // would tell the pump that B's first segments had already been sent.
        if (currentCallRef.current !== callControlId) return;
        // The segments count as delivered either way — only a view that an edit
        // has already overtaken is dropped. Re-sending them would cost a second
        // extraction of words the extractor has already read.
        pushedThroughRef.current = highest;
        if (flush) flushedThroughRef.current = highest;
        if (saveSeqRef.current === seqAtStart) setIntake(next);
      } catch {
        // Leave pushedThrough where it is so these segments go again with the
        // next batch: the extractor drops what it has already read, so a retry
        // costs nothing and a lost batch would cost the words in it.
      } finally {
        // The swap already reset these; a response from the old call must not
        // undo that, or reopen the gate for a queue belonging to a conversation
        // that is over.
        if (currentCallRef.current === callControlId) {
          inFlightRef.current = false;
          setBusy(false);
          const queued = queuedFlushRef.current;
          if (queued !== null) {
            queuedFlushRef.current = null;
            void pump(queued);
          }
        }
      }
    },
    [callControlId, enabled],
  );

  // Two timers off the same signal: push shortly after speech settles, and — if
  // the silence lasts — ask the extractor to read what it is still holding.
  useEffect(() => {
    if (!enabled || !callControlId) return;
    const push = setTimeout(() => void pump(false), PUSH_DEBOUNCE_MS);
    const flush = setTimeout(() => void pump(true), SILENCE_FLUSH_MS);
    return () => {
      clearTimeout(push);
      clearTimeout(flush);
    };
  }, [callControlId, enabled, pump, segments]);

  const save = useCallback(
    async (edits: IntakeEdits) => {
      if (!callControlId) return false;
      setSaving(true);
      try {
        const next = await callIntakeApi.update(callControlId, edits);
        saveSeqRef.current += 1;
        setIntake(next);
        return true;
      } catch {
        // Reported by returning false, not swallowed: an agent whose correction
        // did not save must not be left looking at it as though it did.
        return false;
      } finally {
        setSaving(false);
      }
    },
    [callControlId],
  );

  return { intake, busy, saving, save };
}

/** Fields worth showing, in the order an agent would ask for them. */
export const INTAKE_FIELD_ORDER = [
  'customerName',
  'address',
  'jobTypeName',
  'issueSummary',
  'urgency',
  'preferredTime',
  'accessNotes',
  'email',
  'poNumber',
] as const;

export const INTAKE_FIELD_LABELS: Record<string, string> = {
  customerName: 'Customer',
  address: 'Address',
  jobTypeName: 'Job type',
  jobTypeId: 'Job type',
  issueSummary: 'Issue',
  urgency: 'Urgency',
  preferredTime: 'Preferred time',
  accessNotes: 'Access',
  email: 'Email',
  poNumber: 'PO number',
};

/** `same_day` → `Same day`, for a value that came from an enum. */
export function prettyIntakeValue(field: string, value: string): string {
  if (field !== 'urgency') return value;
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
}
